import React from 'react';
import { motion } from 'framer-motion';
import { useCart } from '../contexts/CartContext';
import { ChevronRightIcon } from 'lucide-react';
interface HeroSectionProps {
  onNavigate?: (page: string) => void;
}
export function HeroSection({
  onNavigate
}: HeroSectionProps) {
  const {
    addToCart
  } = useCart();
  const handleOrderNow = (item: any) => {
    addToCart({
      id: item.id,
      name: item.name,
      price: item.price,
      emoji: item.emoji,
      image: item.image
    });
    if (onNavigate) {
      onNavigate('cart');
    }
  };
  const deals = {
    featured: {
      id: 'main-4',
      name: 'Jollof Rice with Meat & Plantain',
      title: 'SIGNATURE JOLLOF',
      desc: 'Classic West African seasoned rice served with tender meat and sweet plantains.',
      price: 18.99,
      emoji: '🍛',
      image: 'https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2022/06/jollog-rice.jpg?fit=1024%2C683&ssl=1',
      bgColor: 'bg-gradient-to-br from-gray-100 to-gray-300',
      textColor: 'text-charcoal'
    },
    topRight: {
      id: 'main-6',
      name: 'Fried Rice with Plantain',
      title: "CHEF'S SPECIAL",
      desc: 'Flavorful fried rice mixed with vegetables & plantains.',
      price: 17.99,
      emoji: '🍛',
      image: 'https://cdn.magicpatterns.com/uploads/8G6DRzwNsPwqffC4uEtBC4/pasted-image.jpg',
      bgColor: 'bg-gradient-to-br from-amber/20 to-amber/40',
      textColor: 'text-charcoal'
    },
    bottomRight: {
      id: 'main-11',
      name: 'Egusi with Swallow',
      title: 'TRADITIONAL',
      desc: 'Rich melon seed soup with assorted meats.',
      price: 18.99,
      emoji: '🥘',
      image: 'https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2022/06/egusi-n-swallow.jpg?fit=1024%2C683&ssl=1',
      bgColor: 'bg-gradient-to-br from-blue-50 to-blue-200',
      textColor: 'text-charcoal'
    },
    banner: {
      id: 'main-1',
      name: 'Jerk Chicken',
      title: 'SPICY JERK CHICKEN',
      desc: 'Authentic Caribbean flavors grilled to perfection.',
      price: 15.99,
      emoji: '🍗',
      image: 'https://i0.wp.com/victorykitchenz.com/wp-content/uploads/2022/06/download-3-scaled.jpg?fit=1920%2C1280&ssl=1',
      bgColor: 'bg-[#0f3b6c]',
      textColor: 'text-white'
    }
  };
  return <section className="w-full bg-cream py-8 md:py-12" data-id="element-81">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-82">
        {/* Top Row: Featured Left + 2 Stacked Right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mb-4 md:mb-6" data-id="element-83">
          {/* Large Featured Card (Left) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`lg:col-span-8 rounded-2xl overflow-hidden relative ${deals.featured.bgColor} shadow-md flex flex-col md:flex-row min-h-[400px] group`} data-id="element-84">
            {/* Image side */}
            <div className="w-full md:w-3/5 relative h-64 md:h-auto overflow-hidden" data-id="element-85">
              <img src={deals.featured.image} alt={deals.featured.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-86" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-gray-200/80 md:hidden" data-id="element-87" />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent to-gray-200/80 hidden md:block right-0 left-auto w-32" data-id="element-88" />
            </div>

            {/* Content side */}
            <div className="w-full md:w-2/5 p-6 md:p-10 flex flex-col justify-center relative z-10 bg-gradient-to-t from-gray-200 via-gray-100 to-transparent md:bg-none" data-id="element-89">
              <h2 className={`font-heading text-4xl md:text-5xl lg:text-6xl uppercase italic font-black tracking-tighter leading-none mb-4 ${deals.featured.textColor} drop-shadow-sm`} data-id="element-90">
                <span className="text-victory-red block text-2xl md:text-3xl tracking-normal not-italic mb-1" data-id="element-91">
                  Signature
                </span>
                Jollof
              </h2>
              <p className={`font-heading text-lg md:text-xl uppercase tracking-wide mb-6 ${deals.featured.textColor} opacity-90`} data-id="element-92">
                {deals.featured.desc}
              </p>

              <div className="mt-auto md:mt-8" data-id="element-93">
                <div className="flex items-baseline gap-1 mb-3" data-id="element-94">
                  <span className="font-heading text-sm uppercase text-victory-red font-bold" data-id="element-95">
                    From
                  </span>
                  <span className="font-heading text-5xl md:text-6xl text-victory-red font-black tracking-tighter drop-shadow-sm" data-id="element-96">
                    ${deals.featured.price}
                  </span>
                </div>
                <button onClick={() => handleOrderNow(deals.featured)} className="bg-victory-red hover:bg-red-700 text-white font-heading text-xl md:text-2xl px-8 py-3 rounded-full shadow-lg transition-transform active:scale-95 uppercase tracking-wider flex items-center justify-center gap-2 w-full sm:w-auto" data-id="element-97">
                  Order Now{' '}
                  <ChevronRightIcon className="w-6 h-6 bg-white/20 rounded-full p-1" data-id="element-98" />
                </button>
                <p className="text-[10px] uppercase font-body mt-2 text-charcoal/50 text-center sm:text-left" data-id="element-99">
                  *T & C's Apply
                </p>
              </div>
            </div>
          </motion.div>

          {/* Two Stacked Cards (Right) */}
          <div className="lg:col-span-4 flex flex-col gap-4 md:gap-6" data-id="element-100">
            {/* Top Right Card */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.1
          }} className={`flex-1 rounded-2xl overflow-hidden relative ${deals.topRight.bgColor} shadow-md flex flex-col min-h-[250px] group`} data-id="element-101">
              <div className="absolute inset-0 h-3/5 overflow-hidden" data-id="element-102">
                <img src={deals.topRight.image} alt={deals.topRight.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-103" />
                <div className="absolute inset-0 bg-gradient-to-t from-amber-100/90 to-transparent" data-id="element-104" />
              </div>

              <div className="relative z-10 p-5 mt-auto flex flex-col items-center text-center" data-id="element-105">
                <h3 className={`font-heading text-3xl uppercase italic font-black tracking-tighter leading-none mb-1 text-white drop-shadow-md`} data-id="element-106">
                  Chef's Special
                </h3>

                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 w-full mt-2 shadow-sm border border-white/50" data-id="element-107">
                  <div className="flex justify-center items-baseline gap-1 mb-2" data-id="element-108">
                    <span className="font-heading text-xs uppercase text-victory-red font-bold" data-id="element-109">
                      From
                    </span>
                    <span className="font-heading text-3xl text-victory-red font-black tracking-tighter" data-id="element-110">
                      ${deals.topRight.price}
                    </span>
                  </div>
                  <button onClick={() => handleOrderNow(deals.topRight)} className="w-full bg-white border-2 border-victory-red text-victory-red hover:bg-victory-red hover:text-white font-heading text-lg px-4 py-1.5 rounded-full transition-colors uppercase tracking-wider flex items-center justify-center gap-1" data-id="element-111">
                    Order Now <ChevronRightIcon className="w-4 h-4" data-id="element-112" />
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Bottom Right Card */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} className={`flex-1 rounded-2xl overflow-hidden relative ${deals.bottomRight.bgColor} shadow-md flex flex-col min-h-[250px] group`} data-id="element-113">
              <div className="absolute inset-0 h-3/5 overflow-hidden" data-id="element-114">
                <img src={deals.bottomRight.image} alt={deals.bottomRight.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-115" />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-100/90 to-transparent" data-id="element-116" />
              </div>

              <div className="relative z-10 p-5 mt-auto flex flex-col items-center text-center" data-id="element-117">
                <h3 className={`font-heading text-3xl uppercase italic font-black tracking-tighter leading-none mb-1 text-blue-600 drop-shadow-sm`} data-id="element-118">
                  Traditional
                </h3>

                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 w-full mt-2 shadow-sm border border-white/50" data-id="element-119">
                  <div className="flex justify-center items-baseline gap-1 mb-2" data-id="element-120">
                    <span className="font-heading text-xs uppercase text-victory-red font-bold" data-id="element-121">
                      From
                    </span>
                    <span className="font-heading text-3xl text-victory-red font-black tracking-tighter" data-id="element-122">
                      ${deals.bottomRight.price}
                    </span>
                  </div>
                  <button onClick={() => handleOrderNow(deals.bottomRight)} className="w-full bg-victory-red hover:bg-red-700 text-white font-heading text-lg px-4 py-1.5 rounded-full transition-colors uppercase tracking-wider flex items-center justify-center gap-1" data-id="element-123">
                    Order Now{' '}
                    <ChevronRightIcon className="w-4 h-4 bg-white/20 rounded-full" data-id="element-124" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Banner Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className={`w-full rounded-2xl overflow-hidden relative ${deals.banner.bgColor} shadow-md flex flex-col md:flex-row items-center min-h-[160px] group`} data-id="element-125">
          {/* Left Text */}
          <div className="w-full md:w-1/4 p-6 text-center md:text-left z-10" data-id="element-126">
            <h3 className="font-heading text-3xl md:text-4xl uppercase italic font-black tracking-tighter leading-none text-white drop-shadow-md" data-id="element-127">
              LARGE
              <br data-id="element-128" />
              COMBO
              <br data-id="element-129" />
              <span className="bg-victory-red text-white text-lg not-italic tracking-widest px-3 py-1 rounded-md inline-block mt-2 transform -rotate-2" data-id="element-130">
                DEAL
              </span>
            </h3>
          </div>

          {/* Center Image */}
          <div className="w-full md:w-2/4 h-48 md:h-full relative overflow-hidden flex justify-center items-center" data-id="element-131">
            <div className="absolute inset-0 bg-gradient-to-r from-[#0f3b6c] via-transparent to-[#0f3b6c] z-10 hidden md:block" data-id="element-132" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f3b6c] via-transparent to-[#0f3b6c] z-10 md:hidden" data-id="element-133" />
            <img src={deals.banner.image} alt={deals.banner.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-80" data-id="element-134" />
          </div>

          {/* Right Content */}
          <div className="w-full md:w-1/4 p-6 flex flex-col items-center md:items-end justify-center z-10 text-center md:text-right" data-id="element-135">
            <p className="font-heading text-lg text-white uppercase tracking-wide mb-2 leading-tight" data-id="element-136">
              {deals.banner.desc}
            </p>
            <div className="flex items-baseline gap-1 mb-3" data-id="element-137">
              <span className="font-heading text-sm uppercase text-white font-bold" data-id="element-138">
                From
              </span>
              <span className="font-heading text-4xl md:text-5xl text-white font-black tracking-tighter drop-shadow-md" data-id="element-139">
                ${deals.banner.price}
              </span>
            </div>
            <button onClick={() => handleOrderNow(deals.banner)} className="bg-victory-red hover:bg-red-700 text-white font-heading text-xl px-8 py-2.5 rounded-full shadow-lg transition-transform active:scale-95 uppercase tracking-wider flex items-center justify-center gap-2" data-id="element-140">
              Order Now{' '}
              <ChevronRightIcon className="w-5 h-5 bg-white/20 rounded-full p-0.5" data-id="element-141" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>;
}